import { useState, useEffect } from "react";
import { haulAPI, verdictAPI } from "@/lib/api";
import { X, Plus } from "@phosphor-icons/react";
import { formatPrice } from "@/lib/utils-app";

export default function AddToHaulModal({ verdict, onClose }) {
  const [hauls, setHauls] = useState([]);
  const [selectedHaul, setSelectedHaul] = useState("");
  const [newHaulName, setNewHaulName] = useState("");
  const [creating, setCreating] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showNewHaul, setShowNewHaul] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadHauls();
  }, []);

  const loadHauls = async () => {
    try {
      const { data } = await haulAPI.list();
      setHauls(data);
      if (data.length > 0) setSelectedHaul(data[0].id);
    } catch {
      // ignore
    }
  };

  const handleCreateHaul = async () => {
    if (!newHaulName.trim()) return;
    setCreating(true);
    try {
      const { data } = await haulAPI.create({ name: newHaulName.trim() });
      setHauls((prev) => [data, ...prev]);
      setSelectedHaul(data.id);
      setShowNewHaul(false);
      setNewHaulName("");
    } catch {
      setError("Failed to create haul");
    } finally {
      setCreating(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedHaul) {
      setError("Select or create a haul first");
      return;
    }
    setAdding(true);
    try {
      // Add item to haul
      await haulAPI.addItem({
        haul_id: selectedHaul,
        verdict_id: verdict.id,
        brand: verdict.brand,
        title: verdict.title_keywords,
        list_price: verdict.list_price_high || verdict.list_price_low || 0,
        platform: verdict.best_platform,
        size: verdict.size,
      });
      // Link verdict to haul
      if (verdict.id) {
        await verdictAPI.setHaul(verdict.id, selectedHaul).catch(() => {});
      }
      onClose();
    } catch {
      setError("Failed to add item");
    } finally {
      setAdding(false);
    }
  };

  const alreadyInHaul = verdict.haul_id;

  return (
    <div
      className="fixed inset-0 z-[20000] bg-black/50 flex items-end"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="add-to-haul-modal"
    >
      <div className="w-full bg-white border-t-4 border-black max-h-[85vh] overflow-y-auto pb-6">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b-2 border-black sticky top-0 bg-white z-10">
          <h3 className="font-heading text-xl font-bold tracking-tight uppercase">
            Add to Haul
          </h3>
          <button onClick={onClose} className="p-2" data-testid="modal-close-btn">
            <X size={24} weight="bold" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Already in haul notice */}
          {alreadyInHaul && (
            <div className="border-2 border-black bg-[#FFEA00] p-3 font-body text-sm font-medium">
              This item is already in a haul. Adding it again will move it to the selected haul.
            </div>
          )}

          {/* Item Summary */}
          <div className="border-2 border-black p-3 bg-neutral-50">
            <div className="font-body text-sm font-semibold truncate">{verdict.title_keywords}</div>
            <div className="font-heading text-lg font-bold mt-1">
              {formatPrice(verdict.list_price_high || verdict.list_price_low)}
            </div>
          </div>

          {error && (
            <div className="border-2 border-black bg-[#FF1744] text-white p-3 font-body text-sm">
              {error}
            </div>
          )}

          {/* Haul Selection */}
          {hauls.length > 0 && !showNewHaul && (
            <div>
              <label className="block font-body text-xs font-semibold tracking-[0.1em] uppercase mb-2">
                Select Haul
              </label>
              <select
                value={selectedHaul}
                onChange={(e) => setSelectedHaul(e.target.value)}
                className="w-full border-2 border-black bg-white p-3 font-body text-lg focus:outline-none focus:ring-4 focus:ring-black/20"
                data-testid="haul-select"
              >
                {hauls.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name} ({h.item_count || 0} items)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* New Haul */}
          {(showNewHaul || hauls.length === 0) && (
            <div className="space-y-3 border-2 border-black p-3">
              <label className="block font-body text-xs font-semibold tracking-[0.1em] uppercase">
                New Haul Name
              </label>
              <input
                type="text"
                value={newHaulName}
                onChange={(e) => setNewHaulName(e.target.value)}
                placeholder="e.g. Saturday Bins, Garage Sale"
                className="w-full border-2 border-black bg-white p-3 font-body focus:outline-none focus:ring-4 focus:ring-black/20"
                data-testid="new-haul-name-input"
              />
              <button
                onClick={handleCreateHaul}
                disabled={creating}
                className="w-full bg-black text-white p-3 font-heading font-bold uppercase tracking-wide border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none transition-all disabled:opacity-50"
                data-testid="create-haul-btn"
              >
                {creating ? "Creating..." : "Create Haul"}
              </button>
            </div>
          )}

          {hauls.length > 0 && !showNewHaul && (
            <button
              onClick={() => setShowNewHaul(true)}
              className="w-full p-3 border-2 border-black flex items-center justify-center gap-2 font-body font-semibold text-sm uppercase tracking-[0.1em] hover:bg-neutral-100"
              data-testid="new-haul-toggle-btn"
            >
              <Plus size={16} weight="bold" />
              New Haul
            </button>
          )}

          {/* Add Button */}
          <button
            onClick={handleAdd}
            disabled={adding || (!selectedHaul && !showNewHaul)}
            className="w-full bg-[#00E676] text-black p-4 font-heading font-black text-lg uppercase tracking-wide border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none transition-all disabled:opacity-50"
            data-testid="confirm-add-to-haul-btn"
          >
            {adding ? "Adding..." : "Add Item"}
          </button>
        </div>
      </div>
    </div>
  );
}
